import * as React from "react";
import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { PlusIcon } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { SearchableSelect, SelectOption } from "@/lib/shadcn/components/searchable-select";
import { Platform } from "@/models";
import { useGetApartmentsQuery } from "@/store/slices/apartments";
import { AddReservationPayload, useAddReservationMutation } from "@/store/booking-management-api";

const PLATFORM_OPTIONS: { value: number; label: string }[] = [
    { value: Platform.Airbnb, label: "Airbnb" },
    { value: Platform.Booking, label: "Booking" },
    { value: Platform.Out, label: "Out" },
];

// A non-negative decimal/integer monetary string. Accepts e.g. "0", "92.5",
// "620.00". Empty string means "not provided" (handled per-field below).
const moneyRegex = /^\d+(\.\d+)?$/;

/**
 * Form schema mirroring the backend binding in
 * `analytics/cmd/api/requests/reservations.go`. Decimals stay as STRINGS end to
 * end (the backend's shopspring parser accepts numeric strings, and keeping them
 * as text avoids JS float drift). Cross-field date ordering is enforced in a
 * `superRefine` so the inline error lands on the right control.
 */
const schema = z
    .object({
        apartmentId: z
            .number({ error: "Select an apartment" })
            .int()
            .gte(0, "Select an apartment"),
        platform: z
            .number({ error: "Select a platform" })
            .int()
            .refine((v) => PLATFORM_OPTIONS.some((p) => p.value === v), "Select a platform"),
        reservationCode: z.string().trim().min(1, "Reservation code is required"),
        guestName: z.string().trim().min(1, "Guest name is required").max(255, "Guest name must be 255 characters or fewer"),
        guestId: z.string().trim().optional(),
        numberOfGuests: z
            .number({ error: "Number of guests is required" })
            .int()
            .gt(0, "Number of guests must be at least 1"),
        actualDaysOfStay: z
            .number({ error: "Actual days of stay is required" })
            .int()
            .gte(0, "Actual days of stay must be 0 or more"),
        bookingDate: z.string().min(1, "Booking date is required"),
        startDate: z.string().min(1, "Start date is required"),
        endDate: z.string().min(1, "End date is required"),
        paymentDate: z.string().optional(),
        receivableIncome: z.string().regex(moneyRegex, "Enter a non-negative amount"),
        amountPaid: z
            .string()
            .optional()
            .refine((v) => v === undefined || v === "" || moneyRegex.test(v), "Enter a non-negative amount"),
        commission: z.string().regex(moneyRegex, "Enter a non-negative amount"),
        cleaningFee: z
            .string()
            .optional()
            .refine((v) => v === undefined || v === "" || moneyRegex.test(v), "Enter a non-negative amount"),
        cleaningCost: z
            .string()
            .optional()
            .refine((v) => v === undefined || v === "" || moneyRegex.test(v), "Enter a non-negative amount"),
        hostingExpense: z
            .string()
            .optional()
            .refine((v) => v === undefined || v === "" || moneyRegex.test(v), "Enter a non-negative amount"),
    })
    .superRefine((data, ctx) => {
        // bookingDate ≤ startDate ≤ endDate (binding: ltefield).
        if (data.bookingDate && data.startDate && data.bookingDate > data.startDate) {
            ctx.addIssue({
                code: "custom",
                path: ["bookingDate"],
                message: "Booking date must be on or before the start date",
            });
        }
        if (data.startDate && data.endDate && data.endDate < data.startDate) {
            ctx.addIssue({
                code: "custom",
                path: ["endDate"],
                message: "End date must be on or after the start date",
            });
        }
        // paymentDate ≥ bookingDate (binding: gtefield), when provided.
        if (data.paymentDate && data.bookingDate && data.paymentDate < data.bookingDate) {
            ctx.addIssue({
                code: "custom",
                path: ["paymentDate"],
                message: "Payment date must be on or after the booking date",
            });
        }
    });

type FormValues = z.infer<typeof schema>;

const DEFAULT_VALUES: Partial<FormValues> = {
    apartmentId: undefined,
    platform: undefined,
    reservationCode: "",
    guestName: "",
    guestId: "",
    numberOfGuests: undefined,
    actualDaysOfStay: undefined,
    bookingDate: "",
    startDate: "",
    endDate: "",
    paymentDate: "",
    receivableIncome: "",
    amountPaid: "",
    commission: "",
    cleaningFee: "",
    cleaningCost: "",
    hostingExpense: "",
};

/** Drop empty optional decimal/date strings so we send `undefined`, not "". */
function optional(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

/**
 * `<input type="date">` yields a date-only "YYYY-MM-DD", but the API binds the
 * reservation date fields as Go `time.Time` from JSON — which ONLY parses RFC3339
 * (date-only 400s server-side; the prod bug). Send UTC midnight: it matches what
 * the Go test DSL's `json.Marshal(time.Time)` emits, and 00:00Z = 03:00 Athens so
 * the stored Athens calendar day is unchanged (ADR-015, reservation-daily-digest).
 */
function toRFC3339Date(dateOnly: string): string {
    return `${dateOnly}T00:00:00Z`;
}

/** RFC3339 form of an optional date field, or undefined when blank. */
function optionalDate(value: string | undefined): string | undefined {
    const trimmed = optional(value);
    return trimmed ? toRFC3339Date(trimmed) : undefined;
}

function toPayload(values: FormValues): AddReservationPayload {
    return {
        reservationCode: values.reservationCode.trim(),
        apartmentId: values.apartmentId,
        platform: values.platform,
        bookingDate: toRFC3339Date(values.bookingDate),
        startDate: toRFC3339Date(values.startDate),
        endDate: toRFC3339Date(values.endDate),
        actualDaysOfStay: values.actualDaysOfStay,
        guestName: values.guestName.trim(),
        guestId: optional(values.guestId),
        numberOfGuests: values.numberOfGuests,
        receivableIncome: values.receivableIncome,
        amountPaid: optional(values.amountPaid),
        paymentDate: optionalDate(values.paymentDate),
        commission: values.commission,
        cleaningFee: optional(values.cleaningFee),
        cleaningCost: optional(values.cleaningCost),
        hostingExpense: optional(values.hostingExpense),
    };
}

/**
 * Pull the server's error message out of an RTK-Query error body
 * ({errors:[{message}]}), or null when it isn't shaped that way — so the form can
 * show WHAT failed instead of a generic "server error".
 */
function serverErrorMessage(err: unknown): string | null {
    if (typeof err !== "object" || err === null || !("data" in err)) return null;
    const { data } = err as { data?: unknown };
    if (typeof data !== "object" || data === null || !("errors" in data)) return null;
    const { errors } = data as { errors?: unknown };
    if (!Array.isArray(errors) || errors.length === 0) return null;
    const first = errors[0] as { message?: unknown };
    return typeof first?.message === "string" ? first.message : null;
}

export interface NewReservationDialogProps {
    /** Called after a successful create so the caller can refresh the grid. */
    onCreated: () => void;
}

export const NewReservationDialog: React.FC<NewReservationDialogProps> = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const { data: apartmentsState } = useGetApartmentsQuery(true);
    const [addReservation, { isLoading }] = useAddReservationMutation();

    const apartmentOptions: SelectOption[] = useMemo(() => {
        if (!apartmentsState) {
            return [];
        }
        return apartmentsState.ids
            .map((id) => apartmentsState.entities[id])
            .filter((a): a is NonNullable<typeof a> => a != null)
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((a) => ({ value: a.id.toString(), label: a.name }));
    }, [apartmentsState]);

    const {
        control,
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: DEFAULT_VALUES,
        mode: "onSubmit",
    });

    const selectedApartmentId = watch("apartmentId");

    const closeAndReset = () => {
        reset(DEFAULT_VALUES);
        setOpen(false);
    };

    const onSubmit = handleSubmit(async (values) => {
        try {
            await addReservation(toPayload(values)).unwrap();
            toast.success(`Reservation ${values.reservationCode.trim()} created`);
            onCreated();
            closeAndReset();
        } catch (err) {
            const serverMsg = serverErrorMessage(err);
            toast.error(
                serverMsg ? `Failed to create reservation: ${serverMsg}` : `Failed to create reservation`,
            );
        }
    });

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    closeAndReset();
                } else {
                    setOpen(true);
                }
            }}
        >
            <DialogTrigger asChild>
                <Button variant="volt" size="sm" data-testid="new-reservation-trigger">
                    <PlusIcon />
                    New reservation
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-reservation-dialog">
                <DialogHeader>
                    <DialogTitle>New reservation</DialogTitle>
                    <DialogDescription>
                        Manually add a reservation that predates platform sync. Taxes, VAT and payout are computed by the
                        server.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} className="grid gap-4" noValidate>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {/* Apartment (searchable combobox) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-apartment">Apartment</Label>
                            <Controller
                                control={control}
                                name="apartmentId"
                                render={({ field }) => (
                                    <SearchableSelect
                                        id="nr-apartment"
                                        data-testid="nr-apartment"
                                        aria-label="Apartment"
                                        variant="outline"
                                        width="w-full"
                                        placeholder="Select apartment"
                                        options={apartmentOptions}
                                        selectedValue={
                                            field.value !== undefined ? field.value.toString() : undefined
                                        }
                                        onChange={(value) => field.onChange(Number(value))}
                                    />
                                )}
                            />
                            {errors.apartmentId && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.apartmentId.message}
                                </p>
                            )}
                        </div>

                        {/* Platform (shadcn Select) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-platform">Platform</Label>
                            <Controller
                                control={control}
                                name="platform"
                                render={({ field }) => (
                                    <Select
                                        // Keep the control strictly controlled: a stable "" when
                                        // unset (Radix would otherwise warn on the uncontrolled→
                                        // controlled switch the first time a value lands).
                                        value={field.value !== undefined ? field.value.toString() : ""}
                                        onValueChange={(value) => field.onChange(Number(value))}
                                    >
                                        <SelectTrigger id="nr-platform" data-testid="nr-platform" aria-label="Platform">
                                            <SelectValue placeholder="Select platform" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {PLATFORM_OPTIONS.map((p) => (
                                                <SelectItem key={p.value} value={p.value.toString()}>
                                                    {p.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.platform && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.platform.message}
                                </p>
                            )}
                        </div>

                        {/* Reservation code */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-reservation-code">Reservation code</Label>
                            <Input
                                id="nr-reservation-code"
                                data-testid="nr-reservation-code"
                                aria-invalid={!!errors.reservationCode}
                                {...register("reservationCode")}
                            />
                            {errors.reservationCode && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.reservationCode.message}
                                </p>
                            )}
                        </div>

                        {/* Guest name */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-guest-name">Guest name</Label>
                            <Input
                                id="nr-guest-name"
                                data-testid="nr-guest-name"
                                aria-invalid={!!errors.guestName}
                                {...register("guestName")}
                            />
                            {errors.guestName && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.guestName.message}
                                </p>
                            )}
                        </div>

                        {/* Guest ID (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-guest-id">Guest ID (optional)</Label>
                            <Input id="nr-guest-id" data-testid="nr-guest-id" {...register("guestId")} />
                        </div>

                        {/* Number of guests */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-num-guests">Number of guests</Label>
                            <Input
                                id="nr-num-guests"
                                data-testid="nr-num-guests"
                                type="number"
                                min={1}
                                aria-invalid={!!errors.numberOfGuests}
                                {...register("numberOfGuests", { setValueAs: (v) => (v === "" ? undefined : Number(v)) })}
                            />
                            {errors.numberOfGuests && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.numberOfGuests.message}
                                </p>
                            )}
                        </div>

                        {/* Booking date */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-booking-date">Booking date</Label>
                            <Input
                                id="nr-booking-date"
                                data-testid="nr-booking-date"
                                type="date"
                                aria-invalid={!!errors.bookingDate}
                                {...register("bookingDate")}
                            />
                            {errors.bookingDate && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.bookingDate.message}
                                </p>
                            )}
                        </div>

                        {/* Start date */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-start-date">Start date</Label>
                            <Input
                                id="nr-start-date"
                                data-testid="nr-start-date"
                                type="date"
                                aria-invalid={!!errors.startDate}
                                {...register("startDate")}
                            />
                            {errors.startDate && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.startDate.message}
                                </p>
                            )}
                        </div>

                        {/* End date */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-end-date">End date</Label>
                            <Input
                                id="nr-end-date"
                                data-testid="nr-end-date"
                                type="date"
                                aria-invalid={!!errors.endDate}
                                {...register("endDate")}
                            />
                            {errors.endDate && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.endDate.message}
                                </p>
                            )}
                        </div>

                        {/* Actual days of stay */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-actual-days">Actual days of stay</Label>
                            <Input
                                id="nr-actual-days"
                                data-testid="nr-actual-days"
                                type="number"
                                min={0}
                                aria-invalid={!!errors.actualDaysOfStay}
                                {...register("actualDaysOfStay", {
                                    setValueAs: (v) => (v === "" ? undefined : Number(v)),
                                })}
                            />
                            {errors.actualDaysOfStay && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.actualDaysOfStay.message}
                                </p>
                            )}
                        </div>

                        {/* Payment date (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-payment-date">Payment date (optional)</Label>
                            <Input
                                id="nr-payment-date"
                                data-testid="nr-payment-date"
                                type="date"
                                aria-invalid={!!errors.paymentDate}
                                {...register("paymentDate")}
                            />
                            {errors.paymentDate && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.paymentDate.message}
                                </p>
                            )}
                        </div>

                        {/* Receivable (gross) income */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-receivable-income">Receivable income (€)</Label>
                            <Input
                                id="nr-receivable-income"
                                data-testid="nr-receivable-income"
                                inputMode="decimal"
                                aria-invalid={!!errors.receivableIncome}
                                {...register("receivableIncome")}
                            />
                            {errors.receivableIncome && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.receivableIncome.message}
                                </p>
                            )}
                        </div>

                        {/* Amount paid (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-amount-paid">Amount paid (€, optional)</Label>
                            <Input
                                id="nr-amount-paid"
                                data-testid="nr-amount-paid"
                                inputMode="decimal"
                                aria-invalid={!!errors.amountPaid}
                                {...register("amountPaid")}
                            />
                            {errors.amountPaid && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.amountPaid.message}
                                </p>
                            )}
                        </div>

                        {/* Commission */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-commission">Commission (€)</Label>
                            <Input
                                id="nr-commission"
                                data-testid="nr-commission"
                                inputMode="decimal"
                                aria-invalid={!!errors.commission}
                                {...register("commission")}
                            />
                            {errors.commission && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.commission.message}
                                </p>
                            )}
                        </div>

                        {/* Cleaning fee (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-cleaning-fee">Cleaning fee (€, optional)</Label>
                            <Input
                                id="nr-cleaning-fee"
                                data-testid="nr-cleaning-fee"
                                inputMode="decimal"
                                aria-invalid={!!errors.cleaningFee}
                                {...register("cleaningFee")}
                            />
                            {errors.cleaningFee && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.cleaningFee.message}
                                </p>
                            )}
                        </div>

                        {/* Cleaning cost (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-cleaning-cost">Cleaning cost (€, optional)</Label>
                            <Input
                                id="nr-cleaning-cost"
                                data-testid="nr-cleaning-cost"
                                inputMode="decimal"
                                aria-invalid={!!errors.cleaningCost}
                                {...register("cleaningCost")}
                            />
                            {errors.cleaningCost && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.cleaningCost.message}
                                </p>
                            )}
                        </div>

                        {/* Hosting expense (optional) */}
                        <div className="grid gap-2">
                            <Label htmlFor="nr-hosting-expense">Hosting expense (€, optional)</Label>
                            <Input
                                id="nr-hosting-expense"
                                data-testid="nr-hosting-expense"
                                inputMode="decimal"
                                aria-invalid={!!errors.hostingExpense}
                                {...register("hostingExpense")}
                            />
                            {errors.hostingExpense && (
                                <p className="text-destructive text-sm" role="alert">
                                    {errors.hostingExpense.message}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Read by tests that assert on the selected apartment value. */}
                    <input type="hidden" data-testid="nr-apartment-value" value={selectedApartmentId ?? ""} readOnly />

                    <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={closeAndReset}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="volt" data-testid="nr-submit" disabled={isLoading}>
                            {isLoading ? "Creating…" : "Create reservation"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default NewReservationDialog;
